import { memo, useState, useEffect } from 'preact/compat';
import { useAppDispatch, useAppSelector } from '../hooks';
import { updateOffer, fetchOfferBuyers } from '../store/offersSlice';
import { selectSelectedOffer } from '../store/selectors';
import OfferSkeleton from './OfferSkeleton';

/** Convert ISO date to YYYY-MM-DD for input[type=date] */
function toInputDate(val: string | null | undefined): string {
    if (!val) return '';
    try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    } catch { return ''; }
}

const Row = memo(({ label, children }: { label: string; children: any }) => (
    <div className="grid grid-cols-[220px_1fr] gap-4 items-center py-2 border-b border-base-200 last:border-0">
        <div className="text-sm font-medium text-base-content/100 truncate pl-2">{label}</div>
        <div>{children}</div>
    </div>
));

const ProtectionPage = memo(() => {
    const dispatch = useAppDispatch();
    const offer = useAppSelector(selectSelectedOffer);
    const refs = useAppSelector(s => s.references.data);
    const [tab, setTab] = useState<'main' | 'details'>('main');
    const [files, setFiles] = useState<any[] | null>(null); // null = loading

    useEffect(() => {
        if (offer) {
            setFiles(null); // reset to loading state
            const code = Math.round(Number(offer.code));
            fetch(`/api/offers/${code}/files`, { headers: { Accept: 'application/json' } })
                .then(r => r.json())
                .then(d => setFiles(d.success ? (d.data || []) : []))
                .catch(() => setFiles([]));
        } else {
            setFiles(null);
        }
    }, [offer?.code]);

    if (!offer) return <OfferSkeleton />;

    const handleFieldBlur = (field: string, value: any) => {
        dispatch(updateOffer({ code: offer.code, data: { [field]: value } })).then(() => {
            if (field === 'buyer_code') {
                dispatch(fetchOfferBuyers({ code: offer.code }));
            }
        });
    };

    const firms = refs?.firms ?? [];
    const countries = refs?.countries ?? [];
    const buyers = refs?.buyers ?? [];
    const statuses = refs?.offer_statuses ?? [];

    return (
        <div className="h-full flex flex-col relative">
            <div className="px-6 py-4 border-b border-base-300 bg-base-100 text-center">
                <h1 className="text-2xl font-bold">{offer.offer_id || `Предложение #${offer.code}`}</h1>
                <p className="text-sm text-base-content/60 mt-1">{offer.vendor_name || '—'}</p>
            </div>

            <div className="px-6 pt-4 flex gap-2 bg-base-100 justify-center">
                <button className={`btn btn-sm ${tab === 'main' ? 'btn-accent' : 'btn-ghost'}`} onClick={() => setTab('main')}>
                    Основные данные
                </button>
                <button className={`btn btn-sm ${tab === 'details' ? 'btn-accent' : 'btn-ghost'}`} onClick={() => setTab('details')}>
                    Детали
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center">
                <div className="max-w-3xl w-full">
                    {tab === 'main' && (
                        <div className="rounded-lg border border-base-300 divide-y divide-base-200 bg-base-100">
                            <Row label="Номер предложения">
                                <span className="text-sm">{offer.offer_id || '—'}</span>
                            </Row>
                            <Row label="Статус">
                                <select
                                    className="select select-sm select-bordered"
                                    value={offer.status_code ?? ''}
                                    onChange={e => handleFieldBlur('status_code', Number((e.target as HTMLSelectElement).value))}
                                >
                                    {statuses.map(s => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                    ))}
                                </select>
                            </Row>
                            <Row label="Поставщик">
                                <select
                                    className="select select-sm select-bordered w-full"
                                    value={offer.vendor_code ?? ''}
                                    onChange={e => handleFieldBlur('vendor_code', Number((e.target as HTMLSelectElement).value))}
                                >
                                    <option value="">—</option>
                                    {firms.map(f => (
                                        <option key={f.code} value={f.code}>{f.name}</option>
                                    ))}
                                </select>
                            </Row>
                            <Row label="Байер">
                                <select
                                    className="select select-sm select-bordered w-full"
                                    value={offer.buyer_code ?? ''}
                                    onChange={e => handleFieldBlur('buyer_code', Number((e.target as HTMLSelectElement).value))}
                                >
                                    <option value="">—</option>
                                    {buyers.map(b => (
                                        <option key={b.code} value={b.code}>{b.buyer_name}</option>
                                    ))}
                                </select>
                            </Row>
                            <Row label="НДС">
                                <input type="number" className="input input-sm input-bordered w-24"
                                    value={offer.nds ?? ''} placeholder="без НДС"
                                    onBlur={e => handleFieldBlur('nds', Number((e.target as HTMLInputElement).value) || null)}
                                />
                            </Row>
                            <Row label="Тип">
                                <select className="select select-sm select-bordered"
                                    value={String(Number(offer.type_offer) || 0)}
                                    onChange={e => handleFieldBlur('type_offer', Number((e.target as HTMLSelectElement).value))}
                                >
                                    <option value="0">Локал</option>
                                    <option value="1">Импорт</option>
                                </select>
                            </Row>
                            <Row label="Торг/скидка">
                                <input type="text" className="input input-sm input-bordered w-48"
                                    value={offer.sale ?? ''} placeholder="без торга"
                                    onBlur={e => handleFieldBlur('sale', (e.target as HTMLInputElement).value || null)}
                                />
                            </Row>
                            <Row label="Страна отгрузки">
                                <select
                                    className="select select-sm select-bordered"
                                    value={offer.country_guid ?? ''}
                                    onChange={e => handleFieldBlur('country_guid', (e.target as HTMLSelectElement).value || null)}
                                >
                                    <option value="">—</option>
                                    {countries.map(c => (
                                        <option key={c.guid || c.code} value={c.guid || c.code}>{c.name}</option>
                                    ))}
                                </select>
                            </Row>
                        </div>
                    )}

                    {tab === 'details' && (
                        <div className="rounded-lg border border-base-300 divide-y divide-base-200 bg-base-100">
                            <Row label="Дата предложения">
                                <input type="date" className="input input-sm input-bordered"
                                    value={toInputDate(offer.dat)}
                                    onChange={e => handleFieldBlur('dat', (e.target as HTMLInputElement).value || null)} />
                            </Row>
                            <Row label="Дата поставки">
                                <input type="date" className="input input-sm input-bordered"
                                    value={toInputDate(offer.dat_supply)}
                                    onChange={e => handleFieldBlur('dat_supply', (e.target as HTMLInputElement).value || null)} />
                            </Row>
                            <Row label="Дата оплаты">
                                <input type="date" className="input input-sm input-bordered"
                                    value={toInputDate(offer.dat_payment)}
                                    onChange={e => handleFieldBlur('dat_payment', (e.target as HTMLInputElement).value || null)} />
                            </Row>
                            <Row label="Файлы предложения">
                                {files === null ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : files.length > 0 ? (
                                    <div className="space-y-1">
                                        {files.map((f: any) => (
                                            <div key={f.id} className="flex items-center gap-2">
                                                <a href={`/api/files/${f.id}/download`} className="link link-accent text-sm truncate">
                                                    {f.original_name}
                                                </a>
                                                <span className="text-xs text-base-content/40">
                                                    {f.file_size ? `${(f.file_size / 1024 / 1024).toFixed(1)} MB` : ''}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-sm text-base-content/50">Не загружены</span>
                                )}
                            </Row>
                            <Row label="Бренд">
                                <span className="text-sm">{offer.offer_brand || '—'}</span>
                            </Row>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default ProtectionPage;
